import React from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from 'react-router-dom';

import {
  businessQuery,
  datamodelDeleteMutation,
  datamodelMutation,
  datamodelQuery,
  datamodelsQuery,
  groupsQuery,
} from '@queries';
import {
  BodyWrapper,
  ActionObject,
  Header,
  Loader,
  MainWrapper,
} from '@components';
import { ChevronRightIcon } from '@svgs';

import { DataModelForm } from '../components/DataModelForm/DataModelForm.jsx';
import { ProjectBreadcrumbs } from '../components/ProjectBreadcrumbs/ProjectBreadcrumbs.jsx';
import ProjectSidebar from '../components/ProjectSidebar/ProjectSidebar.jsx';

export const DataModel = () => {
  const { t, i18n } = useTranslation();
  const { businessID, datamodelID } = useParams();
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  // get business
  const { data: businessData, isPending: businessIsPending } = useQuery(
    businessQuery(businessID),
  );

  // get datamodel
  const { data: datamodelData, isPending: datamodelIsPending } = useQuery(
    datamodelQuery(datamodelID, {
      staleTime: Infinity,
    }),
  );

  // save datamodel
  const mutation = useMutation(
    datamodelMutation(datamodelID, {
      onSuccess: () => {
        // invalidate the query so it is fetched again
        queryClient.invalidateQueries({
          queryKey: ['datamodels', 'detail', datamodelID],
        });
      },
    }),
  );

  // delete datamodel
  const deleteMutation = useMutation(
    datamodelDeleteMutation(datamodelID, {
      onSuccess: () => {
        // invalidate the lists so its purged
        queryClient.invalidateQueries({ queryKey: ['datamodels', 'list'] });
        // navigate to the list view
        navigate(`/dashboard/projects/${businessID}/datamodels`);
      },
    }),
  );

  // get groups
  const { data: groupsData, isPending: groupsIsPending } =
    useQuery(groupsQuery());

  // get datamodels
  const { data: datamodelsData, isPending: datamodelsIsPending } = useQuery(
    datamodelsQuery({
      order_by: 'name',
    }),
  );

  const modifyData = (data) => {
    // update values before passing to the form
    //
    // update each field.groups to be a list of group IDs. Example: [1234, 1235]
    const defaultData = {
      ...data,
      fields: data.fields.map((field) => {
        return {
          ...field,
          groups: field.groups.map((group) => group.group.id),
        };
      }),
    };
    return defaultData;
  };

  return (
    <>
      <Header />
      <BodyWrapper>
        <ProjectSidebar businessID={businessID} businessData={businessData} />
        <MainWrapper>
          <ProjectBreadcrumbs businessData={businessData}>
            <li className="sb-flex">
              <Link to={`/dashboard/projects/${businessID}/datamodels`}>
                Manage fields
              </Link>
              <ChevronRightIcon />
            </li>
            <li className="sb-flex">
              {datamodelIsPending && <span>...</span>}
              {!datamodelIsPending && <span>{datamodelData.name}</span>}
            </li>
          </ProjectBreadcrumbs>
          <div className="sb-manage-content">
            {(datamodelIsPending || datamodelsIsPending || groupsIsPending) && (
              <>
                <h1>...</h1>
                <Loader className="sb-loader sb-loader-large" />
              </>
            )}
            {!datamodelIsPending &&
              !datamodelsIsPending &&
              !groupsIsPending && (
                <>
                  <h1>{datamodelData.name}</h1>
                  <DataModelForm
                    datamodelData={modifyData(datamodelData)}
                    disabled={mutation.isPending || deleteMutation.isPending}
                    mutation={mutation}
                    groupsData={groupsData}
                    datamodelsData={datamodelsData}
                  />
                  <div className="sb-message-action-section">
                    <ActionObject
                      onAction={() => deleteMutation.mutate()}
                      loading={deleteMutation.isPending}
                      actionName="Delete"
                      actionVerb="Deleting"
                      label="Delete fields"
                      note="This will be permanently deleted"
                    />
                  </div>
                </>
              )}
          </div>
        </MainWrapper>
      </BodyWrapper>
    </>
  );
};
